/**
 * Limited shop item duration helpers.
 * Accepts strings like "1d", "2d 15h", "45m", "1d 2h 30m".
 */

export const LIMITED_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

const DURATION_TOKEN =
  /(\d+)\s*(d|day|days|h|hr|hrs|hour|hours|m|min|mins|minute|minutes|s|sec|secs|second|seconds)\b/gi;

/** Parse a human duration into milliseconds. Returns null if empty/invalid. */
export function parseLimitedDurationMs(raw: unknown): number | null {
  const s = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (!s) return null;
  let total = 0;
  let matched = false;
  DURATION_TOKEN.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = DURATION_TOKEN.exec(s))) {
    matched = true;
    const n = Number(m[1]);
    if (!Number.isFinite(n) || n < 0) return null;
    const u = m[2];
    if (u.startsWith("d")) total += n * 86_400_000;
    else if (u.startsWith("h")) total += n * 3_600_000;
    else if (u.startsWith("m")) total += n * 60_000;
    else total += n * 1_000;
  }
  if (!matched) return null;
  // Reject leftover non-whitespace junk (e.g. "2d foo").
  const stripped = s.replace(DURATION_TOKEN, "").replace(/[\s,]+/g, "");
  if (stripped.length) return null;
  if (total <= 0) return null;
  return total;
}

/** Compact remaining/absolute duration label: "2d 15h", "45m", "<1m". */
export function formatDurationMs(ms: number): string {
  const t = Math.max(0, Math.floor(ms));
  if (t < 60_000) return "<1m";
  const days = Math.floor(t / 86_400_000);
  const hours = Math.floor((t % 86_400_000) / 3_600_000);
  const mins = Math.floor((t % 3_600_000) / 60_000);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0 && days === 0) parts.push(`${mins}m`);
  if (!parts.length) return "<1m";
  return parts.join(" ");
}

export function formatEndsInLabel(endsAtIso: string | Date, nowMs = Date.now()): string {
  const ends = endsAtIso instanceof Date ? endsAtIso.getTime() : Date.parse(endsAtIso);
  if (!Number.isFinite(ends)) return "—";
  return formatDurationMs(ends - nowMs);
}

export type LimitedShopPhase = "active" | "cooldown" | "expired" | "none";

export function limitedShopPhase(
  opts: { limitedItem?: boolean | null; limitedEndsAt?: string | Date | null },
  nowMs = Date.now(),
): LimitedShopPhase {
  if (!opts.limitedItem || !opts.limitedEndsAt) return "none";
  const ends =
    opts.limitedEndsAt instanceof Date
      ? opts.limitedEndsAt.getTime()
      : Date.parse(String(opts.limitedEndsAt));
  if (!Number.isFinite(ends)) return "none";
  if (nowMs < ends) return "active";
  if (nowMs < ends + LIMITED_COOLDOWN_MS) return "cooldown";
  return "expired";
}

/** Visible in Shop → Items (active sale or 1-week ended cooldown). */
export function limitedItemVisibleInShop(
  opts: { limitedItem?: boolean | null; limitedEndsAt?: string | Date | null },
  nowMs = Date.now(),
): boolean {
  const phase = limitedShopPhase(opts, nowMs);
  return phase === "none" || phase === "active" || phase === "cooldown";
}

/** Purchasable / claimable / giftable. */
export function limitedItemPurchasable(
  opts: { limitedItem?: boolean | null; limitedEndsAt?: string | Date | null },
  nowMs = Date.now(),
): boolean {
  const phase = limitedShopPhase(opts, nowMs);
  return phase === "none" || phase === "active";
}
